import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { albumService } from '../../services/api';
import { useAlbumTrades } from '../hooks/useAlbumTrades';
import TradeAcceptedModal from './TradeAcceptedModal';

/**
 * Panel del sidebar slim de /album. Tiene 6 variantes (cycler RIGHTBAR):
 *
 *   0  Baseline           — comportamiento actual (chips + 3 cards + expand).
 *   1  Single Peek        — 1 card prominente + segunda fadeada con CTA grande.
 *   2  Boxed Group        — todo lo del baseline envuelto en una caja sutil.
 *   3  Stacked Tabs       — filtros tipo segmented (Recibidos/Esperando/Listos).
 *   4  Action First       — sólo accionables prominentes, esperando colapsado.
 *   5  Inbox List         — filas finas tipo bandeja de entrada.
 *
 * Cada variante comparte hooks/handlers (useAlbumTrades, accept/reject/...) y
 * sólo cambia la presentación. La 0 queda 1:1 igual al original.
 */
export const RIGHTBAR_VARIANT_NAMES = [
  'Boxed Group',
  'Single Peek',
  'Baseline (actual)',
  'Stacked Tabs',
  'Action First',
  'Inbox List',
  'CTA Bold',
  'CTA Banner',
  'Hero CTA + Soft Swap',
  'Boxed + botón color',
  'Boxed + botón hero',
  'Solo chips + botón hero',
];

const MAX_VISIBLE = 3;

function AlbumTradesPanel({ myCommunities, compact: _compact = false, variant = 0, selectedCommunityIds, onExpand }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const trades = useAlbumTrades();
  const [acceptedOpen, setAcceptedOpen] = useState(null);
  const [busyId, setBusyId] = useState(null);

  // Pending (incoming/outgoing): abrir el trade en TRADE_REVIEW navegando con
  // ?tradeId=X — mismo flow que el openTrade del dashboard. Las accepted siguen
  // abriendo el TradeAcceptedModal local porque ese modal tiene UI específica
  // de "Coordinar" (contacto, completar) que no aplica a pending.
  const openPending = useCallback((trade) => {
    if (!trade?.id) return;
    navigate(`/album?tradeId=${trade.id}`);
  }, [navigate]);

  const communityNameById = useMemo(() => {
    const m = new Map();
    for (const c of myCommunities || []) m.set(c.id, c.name);
    return m;
  }, [myCommunities]);

  // Filtro por comunidad: cuando hay comunidades seleccionadas, sólo
  // mostrar trades cuyo `otherUser` tenga al menos una comunidad
  // compartida (datos en bulkCache.albums[uid].c).
  const filterByCommunity = useCallback((arr) => {
    if (!Array.isArray(selectedCommunityIds) || selectedCommunityIds.length === 0) return arr;
    const set = new Set(selectedCommunityIds);
    const albums = trades.bulkCache?.albums || {};
    return arr.filter(tr => {
      const uid = tr.otherUser?.id;
      if (uid == null) return false;
      const cs = albums[uid]?.c;
      if (!Array.isArray(cs)) return false;
      return cs.some(c => set.has(c));
    });
  }, [selectedCommunityIds, trades.bulkCache]);

  const merged = useMemo(() => {
    const tag = (arr, kind) => arr.map(tr => ({ ...tr, _kind: kind }));
    return [
      ...tag(filterByCommunity(trades.incomingTrades), 'incoming'),
      ...tag(filterByCommunity(trades.activeTrades),   'active'),
      ...tag(filterByCommunity(trades.outgoingTrades), 'waiting'),
    ];
  }, [trades.incomingTrades, trades.activeTrades, trades.outgoingTrades, filterByCommunity]);

  const visible = merged.slice(0, MAX_VISIBLE);

  const handleAccept = async (trade) => {
    setBusyId(trade.id);
    try { await albumService.acceptTrade(trade.id); await trades.refresh(); }
    finally { setBusyId(null); }
  };
  const handleReject = async (trade) => {
    setBusyId(trade.id);
    try { await albumService.rejectTrade(trade.id); await trades.refresh(); }
    finally { setBusyId(null); }
  };
  const handleCounter = (trade) => {
    // Importante: NO cancelamos la propuesta original todavía. Eso pasa
    // recién al enviar la contraoferta (ver handleProposeTrade en
    // StickerAlbum.js, que mira `pendingCounterOf` y rejecta atómicamente
    // antes de proponer la nueva). Si el user abandona el flujo, la
    // propuesta original sigue activa intacta.
    window.dispatchEvent(new CustomEvent('album:openTradeMatch', {
      detail: {
        userId: trade.otherUser?.id,
        offering: trade.youReceive || [],
        requesting: trade.youGive || [],
        originalTradeId: trade.id,
      },
    }));
  };

  const remaining = Math.max(0, merged.length - visible.length);

  // ── Empty state — compartido por todas las variantes ──────────────────
  if (merged.length === 0 && !trades.loading) {
    return (
      <section className="mb-6">
        <h4 className="text-xs font-bold uppercase tracking-widest mb-3 text-on-surface-variant">
          {t('Mis intercambios')}
        </h4>
        <p className="text-[11px] text-on-surface-variant px-1">
          {t('No tenés intercambios activos. Tocá un coleccionista para empezar.')}
        </p>
        {onExpand && (
          <button
            type="button"
            onClick={onExpand}
            className="mt-2 w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-[11px] font-bold border border-outline-variant text-on-surface-variant hover:bg-surface-container transition-colors"
          >
            <span className="material-symbols-outlined text-base">unfold_more</span>
            {t('Ver todos mis intercambios')}
          </button>
        )}
      </section>
    );
  }

  const cardProps = {
    busyId, onOpenAccepted: setAcceptedOpen, onOpenPending: openPending, handleAccept, handleReject, handleCounter,
  };

  let body;
  if (variant === 1)       body = <SinglePeek visible={visible} merged={merged} onExpand={onExpand} t={t} {...cardProps} />;
  else if (variant === 2)  body = <Baseline visible={visible} merged={merged} trades={trades} remaining={remaining} onExpand={onExpand} t={t} {...cardProps} />;
  else if (variant === 3)  body = <StackedTabs merged={merged} trades={trades} onExpand={onExpand} t={t} {...cardProps} />;
  else if (variant === 4)  body = <ActionFirst merged={merged} onExpand={onExpand} t={t} {...cardProps} />;
  else if (variant === 5)  body = <InboxList merged={merged} onExpand={onExpand} t={t} {...cardProps} />;
  else if (variant === 6)  body = <CtaBold visible={visible} merged={merged} trades={trades} remaining={remaining} onExpand={onExpand} t={t} {...cardProps} />;
  else if (variant === 7)  body = <CtaBanner visible={visible} merged={merged} trades={trades} remaining={remaining} onExpand={onExpand} t={t} {...cardProps} />;
  else if (variant === 8)  body = <CtaBold visible={visible} merged={merged} trades={trades} remaining={remaining} onExpand={onExpand} t={t} {...cardProps} />;
  else if (variant === 9)  body = <BoxedGroup visible={visible} merged={merged} trades={trades} onExpand={onExpand} t={t} buttonStyle="solid" {...cardProps} />;
  else if (variant === 10) body = <BoxedGroup visible={visible} merged={merged} trades={trades} onExpand={onExpand} t={t} buttonStyle="bold-small" {...cardProps} />;
  else if (variant === 11) body = <ChipsHero trades={trades} merged={merged} onExpand={onExpand} t={t} />;
  else                     body = <BoxedGroup visible={visible} merged={merged} trades={trades} onExpand={onExpand} t={t} {...cardProps} />;

  return (
    <>
      {body}
      {acceptedOpen && (
        <TradeAcceptedModal
          trade={acceptedOpen}
          communityNameById={communityNameById}
          onClose={() => setAcceptedOpen(null)}
          onAfterAction={trades.refresh}
        />
      )}
    </>
  );
}

/* ── Variants ────────────────────────────────────────────────────────── */

function PanelHeader({ t, count }) {
  return (
    <h4 className="text-xs font-bold uppercase tracking-widest mb-3 text-on-surface-variant flex items-center gap-2">
      {t('Mis intercambios')}
      {typeof count === 'number' && count > 0 && (
        <span className="text-[10px] font-black bg-surface-container-high text-on-surface-variant rounded-full px-1.5 py-0.5">{count}</span>
      )}
    </h4>
  );
}

function ExpandButton({ t, onExpand, label, prominent = false, whiteBg = false, style = 'default' }) {
  if (!onExpand) return null;

  const text = label || t('Ver todos mis intercambios');

  if (style === 'bold') {
    return (
      <button
        type="button"
        onClick={onExpand}
        className="mt-3 w-full flex items-center justify-center gap-2 px-3 py-3 rounded-xl text-sm font-black bg-primary text-on-primary shadow-md shadow-primary/30 hover:brightness-110 active:scale-[0.99] transition-all"
      >
        <span className="material-symbols-outlined text-base">dashboard</span>
        {text}
        <span className="material-symbols-outlined text-base">arrow_forward</span>
      </button>
    );
  }

  if (style === 'bold-small') {
    return (
      <button
        type="button"
        onClick={onExpand}
        className="mt-3 w-full flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl text-xs font-black bg-primary text-on-primary shadow-sm shadow-primary/20 hover:brightness-110 active:scale-[0.99] transition-all"
      >
        <span className="material-symbols-outlined text-sm">dashboard</span>
        {text}
        <span className="material-symbols-outlined text-sm">arrow_forward</span>
      </button>
    );
  }

  if (style === 'solid') {
    return (
      <button
        type="button"
        onClick={onExpand}
        className="mt-3 w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-[11px] font-bold bg-primary text-on-primary hover:brightness-110 transition-all"
      >
        <span className="material-symbols-outlined text-base">unfold_more</span>
        {text}
      </button>
    );
  }

  const cls = prominent
    ? `border-primary/40 text-primary ${whiteBg ? 'bg-white' : 'bg-primary/5'} hover:bg-primary/10`
    : `border-outline-variant text-on-surface-variant ${whiteBg ? 'bg-white' : ''} hover:bg-surface-container`;
  return (
    <button
      type="button"
      onClick={onExpand}
      className={`mt-3 w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-[11px] font-bold border transition-colors ${cls}`}
    >
      <span className="material-symbols-outlined text-base">unfold_more</span>
      {text}
    </button>
  );
}

/* Banner CTA — card ancha con stats inline + flecha. */
function ExpandBanner({ t, onExpand, trades, totalMerged }) {
  if (!onExpand) return null;
  const incoming = trades?.incomingTrades?.length ?? 0;
  const ready = trades?.activeTrades?.length ?? 0;
  return (
    <button
      type="button"
      onClick={onExpand}
      className="mt-3 w-full rounded-xl bg-gradient-to-r from-primary to-primary-fixed-dim text-on-primary px-4 py-3 flex items-center gap-3 shadow-md shadow-primary/30 hover:brightness-110 active:scale-[0.99] transition-all text-left"
    >
      <span className="material-symbols-outlined text-xl">dashboard</span>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-black truncate">{t('Ver todos mis intercambios')}</p>
        <p className="text-[10px] font-bold uppercase tracking-wider opacity-90">
          {totalMerged} {t('en total')}
          {incoming > 0 && ` · ${incoming} ${t('por aceptar')}`}
          {ready > 0 && ` · ${ready} ${t('listos')}`}
        </p>
      </div>
      <span className="material-symbols-outlined text-xl">arrow_forward</span>
    </button>
  );
}

function CountChips({ trades, t }) {
  return (
    <div className="grid grid-cols-3 gap-1.5 mb-3">
      <CountChip color="amber"   label={t('Recibidos')} count={trades.incomingTrades.length} />
      <CountChip color="neutral" label={t('Esperando')} count={trades.outgoingTrades.length} />
      <CountChip color="primary" label={t('Listos')}    count={trades.activeTrades.length} />
    </div>
  );
}

function Baseline({ visible, merged, trades, remaining, onExpand, t, busyId, onOpenAccepted, onOpenPending, handleAccept, handleReject, handleCounter }) {
  return (
    <section className="mb-6">
      <PanelHeader t={t} />
      <CountChips trades={trades} t={t} />
      <div className="space-y-2">
        {visible.map(tr => (
          <CompactCard key={tr.id} trade={tr} busy={busyId === tr.id}
            onOpenAccepted={() => onOpenAccepted(tr)}
            onOpenPending={() => onOpenPending(tr)}
            onAccept={() => handleAccept(tr)} onReject={() => handleReject(tr)} onCounter={() => handleCounter(tr)} />
        ))}
      </div>
      {remaining > 0 && (
        <p className="mt-2 text-[10px] text-on-surface-variant text-center">
          +{remaining} {t('más en el dashboard')}
        </p>
      )}
      <ExpandButton t={t} onExpand={onExpand} prominent />
    </section>
  );
}

function SinglePeek({ visible, merged, onExpand, t, busyId, onOpenAccepted, onOpenPending, handleAccept, handleReject, handleCounter }) {
  const top = visible[0];
  const peek = visible[1];
  const total = merged.length;
  return (
    <section className="mb-6">
      <PanelHeader t={t} count={total} />
      {top && (
        <CompactCard trade={top} busy={busyId === top.id}
          onOpenAccepted={() => onOpenAccepted(top)}
          onOpenPending={() => onOpenPending(top)}
          onAccept={() => handleAccept(top)} onReject={() => handleReject(top)} onCounter={() => handleCounter(top)} />
      )}
      {peek && (
        <div className="relative -mt-1 px-2">
          <div className="rounded-xl border border-outline-variant/60 bg-surface-container/50 px-2.5 pt-2 pb-3 opacity-70" style={{ maxHeight: 36, overflow: 'hidden' }}>
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-full flex items-center justify-center text-white text-[10px] font-bold shrink-0"
                   style={{ backgroundColor: peek.otherUser?.color || '#6b7280' }}>
                {peek.otherUser?.initial || '?'}
              </div>
              <span className="flex-1 min-w-0 text-[12px] font-bold text-on-surface truncate">
                {peek.otherUser?.name}
              </span>
            </div>
          </div>
          <div className="absolute inset-x-0 bottom-0 h-6 bg-gradient-to-b from-transparent to-surface pointer-events-none" />
        </div>
      )}
      {total > 1 && (
        <button
          type="button"
          onClick={onExpand}
          className="mt-3 w-full flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl text-xs font-bold border border-primary/40 text-primary bg-primary/5 hover:bg-primary/10 transition-colors"
        >
          <span className="material-symbols-outlined text-base">expand_more</span>
          {t('Ver los {{n}} intercambios', { n: total })}
        </button>
      )}
      {total <= 1 && <ExpandButton t={t} onExpand={onExpand} prominent />}
    </section>
  );
}

function BoxedGroup({ visible, merged, trades, onExpand, t, busyId, onOpenAccepted, onOpenPending, handleAccept, handleReject, handleCounter, buttonStyle = 'default' }) {
  const top = visible[0];
  const remaining = Math.max(0, merged.length - (top ? 1 : 0));
  // Default: el botón mantiene el estilo histórico (border + whiteBg) para
  // que se distinga del fondo gris. solid/bold-small lo pintan sólido.
  const buttonProps = buttonStyle === 'solid'
    ? { style: 'solid' }
    : buttonStyle === 'bold-small'
      ? { style: 'bold-small' }
      : { prominent: true, whiteBg: true };
  return (
    <section className="mb-6">
      <PanelHeader t={t} />
      {/* Caja gris (mismo bg-surface-container que la card de Intercambio
          rápido) para agrupar visualmente sin meter color. Padding mínimo
          horizontal para que las cards mantengan ancho. */}
      <div className="rounded-2xl bg-surface-container shadow-sm py-3 px-2">
        <CountChips trades={trades} t={t} />
        {top && (
          <CompactCard trade={top} busy={busyId === top.id} whiteBg
            onOpenAccepted={() => onOpenAccepted(top)}
          onOpenPending={() => onOpenPending(top)}
            onAccept={() => handleAccept(top)} onReject={() => handleReject(top)} onCounter={() => handleCounter(top)} />
        )}
        {remaining > 0 && (
          <p className="mt-2 text-[10px] text-on-surface-variant text-center">
            +{remaining} {t('más en el dashboard')}
          </p>
        )}
        <ExpandButton t={t} onExpand={onExpand} {...buttonProps} />
      </div>
    </section>
  );
}

/* Solo chips + botón hero — sin cards de trades, mínima visual. */
function ChipsHero({ trades, merged, onExpand, t }) {
  return (
    <section className="mb-6">
      <PanelHeader t={t} count={merged.length} />
      <CountChips trades={trades} t={t} />
      <ExpandButton t={t} onExpand={onExpand} style="bold" />
    </section>
  );
}

function StackedTabs({ merged, trades, onExpand, t, busyId, onOpenAccepted, onOpenPending, handleAccept, handleReject, handleCounter }) {
  const [tab, setTab] = useState(() => trades.incomingTrades.length > 0 ? 'incoming' : trades.activeTrades.length > 0 ? 'active' : 'waiting');
  const filtered = useMemo(() => merged.filter(tr => tr._kind === tab), [merged, tab]);
  const slice = filtered.slice(0, MAX_VISIBLE);
  const remaining = Math.max(0, filtered.length - slice.length);

  const TABS = [
    { id: 'incoming', label: t('Recibidos'), n: trades.incomingTrades.length },
    { id: 'active',   label: t('Listos'),    n: trades.activeTrades.length },
    { id: 'waiting',  label: t('Esperando'), n: trades.outgoingTrades.length },
  ];

  return (
    <section className="mb-6">
      <PanelHeader t={t} count={merged.length} />
      <div className="flex bg-surface-container rounded-lg p-0.5 mb-3">
        {TABS.map(({ id, label, n }) => (
          <button key={id} type="button" onClick={() => setTab(id)}
            className={`flex-1 py-1.5 px-1 rounded-md text-[10px] font-black uppercase tracking-wide transition-colors ${
              tab === id ? 'bg-surface text-on-surface shadow-sm' : 'text-on-surface-variant hover:text-on-surface'
            }`}>
            {label}
            <span className="ml-1 text-[10px] opacity-70">{n}</span>
          </button>
        ))}
      </div>
      {slice.length > 0 ? (
        <div className="space-y-2">
          {slice.map(tr => (
            <CompactCard key={tr.id} trade={tr} busy={busyId === tr.id}
              onOpenAccepted={() => onOpenAccepted(tr)}
            onOpenPending={() => onOpenPending(tr)}
              onAccept={() => handleAccept(tr)} onReject={() => handleReject(tr)} onCounter={() => handleCounter(tr)} />
          ))}
        </div>
      ) : (
        <p className="text-[11px] text-on-surface-variant text-center py-4">
          {t('Sin intercambios en esta categoría')}
        </p>
      )}
      {remaining > 0 && (
        <p className="mt-2 text-[10px] text-on-surface-variant text-center">
          +{remaining} {t('más en el dashboard')}
        </p>
      )}
      <ExpandButton t={t} onExpand={onExpand} prominent />
    </section>
  );
}

function ActionFirst({ merged, onExpand, t, busyId, onOpenAccepted, onOpenPending, handleAccept, handleReject, handleCounter }) {
  const action = merged.filter(tr => tr._kind === 'incoming' || tr._kind === 'active');
  const waiting = merged.filter(tr => tr._kind === 'waiting');
  const slice = action.slice(0, MAX_VISIBLE);
  const remaining = Math.max(0, action.length - slice.length);

  return (
    <section className="mb-6">
      <PanelHeader t={t} count={merged.length} />
      {slice.length > 0 ? (
        <div className="space-y-2">
          {slice.map(tr => (
            <CompactCard key={tr.id} trade={tr} busy={busyId === tr.id}
              onOpenAccepted={() => onOpenAccepted(tr)}
            onOpenPending={() => onOpenPending(tr)}
              onAccept={() => handleAccept(tr)} onReject={() => handleReject(tr)} onCounter={() => handleCounter(tr)} />
          ))}
        </div>
      ) : (
        <p className="text-[11px] text-on-surface-variant px-1 py-2">
          {t('Sin acciones pendientes')}
        </p>
      )}
      {remaining > 0 && (
        <p className="mt-2 text-[10px] text-on-surface-variant text-center">
          +{remaining} {t('más en el dashboard')}
        </p>
      )}
      {waiting.length > 0 && (
        <button
          type="button"
          onClick={onExpand}
          className="mt-3 w-full flex items-center justify-between gap-2 px-3 py-2 rounded-xl text-[11px] font-bold border border-outline-variant/60 bg-surface-container/40 text-on-surface-variant hover:bg-surface-container transition-colors"
        >
          <span className="flex items-center gap-1.5">
            <span className="material-symbols-outlined text-sm">schedule</span>
            {t('{{n}} esperando respuesta', { n: waiting.length })}
          </span>
          <span className="material-symbols-outlined text-sm">chevron_right</span>
        </button>
      )}
      <ExpandButton t={t} onExpand={onExpand} prominent />
    </section>
  );
}

function CtaBold({ visible, merged, trades, remaining, onExpand, t, busyId, onOpenAccepted, onOpenPending, handleAccept, handleReject, handleCounter }) {
  return (
    <section className="mb-6">
      <PanelHeader t={t} count={merged.length} />
      <CountChips trades={trades} t={t} />
      <div className="space-y-2">
        {visible.map(tr => (
          <CompactCard key={tr.id} trade={tr} busy={busyId === tr.id}
            onOpenAccepted={() => onOpenAccepted(tr)}
            onOpenPending={() => onOpenPending(tr)}
            onAccept={() => handleAccept(tr)} onReject={() => handleReject(tr)} onCounter={() => handleCounter(tr)} />
        ))}
      </div>
      {remaining > 0 && (
        <p className="mt-2 text-[10px] text-on-surface-variant text-center">
          +{remaining} {t('más en el dashboard')}
        </p>
      )}
      <ExpandButton t={t} onExpand={onExpand} style="bold" />
    </section>
  );
}

function CtaBanner({ visible, merged, trades, remaining, onExpand, t, busyId, onOpenAccepted, onOpenPending, handleAccept, handleReject, handleCounter }) {
  return (
    <section className="mb-6">
      <PanelHeader t={t} count={merged.length} />
      <div className="space-y-2">
        {visible.map(tr => (
          <CompactCard key={tr.id} trade={tr} busy={busyId === tr.id}
            onOpenAccepted={() => onOpenAccepted(tr)}
            onOpenPending={() => onOpenPending(tr)}
            onAccept={() => handleAccept(tr)} onReject={() => handleReject(tr)} onCounter={() => handleCounter(tr)} />
        ))}
      </div>
      {remaining > 0 && (
        <p className="mt-2 text-[10px] text-on-surface-variant text-center">
          +{remaining} {t('más en el dashboard')}
        </p>
      )}
      <ExpandBanner t={t} onExpand={onExpand} trades={trades} totalMerged={merged.length} />
    </section>
  );
}

function InboxList({ merged, onExpand, t, busyId, onOpenAccepted, onOpenPending, handleAccept, handleReject, handleCounter }) {
  const slice = merged.slice(0, 5);
  const remaining = Math.max(0, merged.length - slice.length);
  return (
    <section className="mb-6">
      <PanelHeader t={t} count={merged.length} />
      <div className="rounded-xl bg-surface-container/40 ring-1 ring-outline-variant/30 divide-y divide-outline-variant/30 overflow-hidden">
        {slice.map(tr => (
          <InboxRow key={tr.id} trade={tr} busy={busyId === tr.id}
            onClick={() => {
              if (tr._kind === 'active') onOpenAccepted(tr);
              else if (tr._kind === 'incoming' || tr._kind === 'waiting') onOpenPending(tr);
              else onExpand?.();
            }}
            t={t} />
        ))}
      </div>
      {remaining > 0 && (
        <p className="mt-2 text-[10px] text-on-surface-variant text-center">
          +{remaining} {t('más en el dashboard')}
        </p>
      )}
      <ExpandButton t={t} onExpand={onExpand} prominent />
    </section>
  );
}

function InboxRow({ trade, busy, onClick, t }) {
  const dotColor = trade._kind === 'incoming' ? '#f59e0b' : trade._kind === 'active' ? '#10b981' : '#9ca3af';
  const giveCount = trade.youGive?.length || 0;
  const receiveCount = trade.youReceive?.length || 0;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      className="w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-surface-container transition-colors disabled:opacity-60"
    >
      <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: dotColor }} />
      <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-[10px] font-bold shrink-0"
           style={{ backgroundColor: trade.otherUser?.color || '#6b7280' }}>
        {trade.otherUser?.initial || '?'}
      </div>
      <span className="flex-1 min-w-0 text-[12px] font-bold text-on-surface truncate">
        {trade.otherUser?.name}
      </span>
      <span className="flex items-center gap-1 text-[10px] tabular-nums">
        {receiveCount > 0 && <span className="font-black text-emerald-700">+{receiveCount}</span>}
        {giveCount > 0    && <span className="font-black text-rose-700">−{giveCount}</span>}
      </span>
    </button>
  );
}

function CountChip({ color, label, count }) {
  const cls = {
    amber:   'bg-amber-100 text-amber-800 border-amber-300',
    neutral: 'bg-surface-container text-on-surface-variant border-outline-variant',
    primary: 'bg-primary/10 text-primary border-primary/40',
  }[color] || 'bg-surface-container text-on-surface-variant border-outline-variant';
  return (
    <div className={`flex flex-col items-center justify-center px-2 py-2 rounded-lg border ${cls}`}>
      <span className="text-base font-black tabular-nums">{count}</span>
      <span className="text-[9px] font-bold uppercase tracking-wider">{label}</span>
    </div>
  );
}

/* ── compact card (compartida por baseline / boxed / tabs / action) ─ */

const STATE_META = {
  incoming: { label: 'Recibido',  icon: 'inbox',        cls: 'bg-amber-100 text-amber-800' },
  active:   { label: 'Listo',     icon: 'check_circle', cls: 'bg-primary text-on-primary' },
  waiting:  { label: 'Esperando', icon: 'schedule',     cls: 'bg-surface-container-highest text-on-surface-variant' },
};

function StateBadge({ kind }) {
  const m = STATE_META[kind] || STATE_META.waiting;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wide ${m.cls}`}>
      <span className="material-symbols-outlined text-[12px]">{m.icon}</span>
      {m.label}
    </span>
  );
}

function CountBadge({ value, color }) {
  if (!value) return null;
  return (
    <span
      className="inline-flex items-center justify-center min-w-[22px] h-5 px-1.5 rounded-md text-[11px] font-black tabular-nums"
      style={{ backgroundColor: `${color}1f`, color }}
    >
      {value}
    </span>
  );
}

function CompactCard({ trade, busy, whiteBg = false, onOpenAccepted, onOpenPending, onAccept, onReject, onCounter }) {
  const { t } = useTranslation();
  const giveCount = trade.youGive?.length || 0;
  const receiveCount = trade.youReceive?.length || 0;

  // Active abre el TradeAcceptedModal (UI de "Coordinar"). Incoming/waiting
  // (pending) navegan al TRADE_REVIEW vía ?tradeId=X. Antes sólo active era
  // clickeable: ahora los pending también, para que el card entero responda
  // y no sólo los botones de Accept/Counter/Reject de adentro.
  const onClick = trade._kind === 'active' ? onOpenAccepted
                : (trade._kind === 'incoming' || trade._kind === 'waiting') ? onOpenPending
                : undefined;
  const isClickable = !!onClick;

  const wrapperBase = 'rounded-xl p-2.5 transition-all';
  const wrapperByKind =
    trade._kind === 'incoming' ? `border-2 border-amber-300 ${whiteBg ? 'bg-white' : 'bg-amber-50/60'} ${isClickable ? 'hover:shadow-md cursor-pointer' : ''}`
    : trade._kind === 'active' ? `border-2 border-primary ${whiteBg ? 'bg-white' : 'bg-primary/5'} hover:shadow-md cursor-pointer`
    :                            `border border-outline-variant ${whiteBg ? 'bg-white' : 'bg-surface-container/60'} ${isClickable ? 'hover:shadow-md cursor-pointer' : ''}`;

  return (
    <div className={`${wrapperBase} ${wrapperByKind}`} onClick={onClick}>
      <div className="flex items-center gap-2">
        <div
          className="w-8 h-8 rounded-full flex items-center justify-center text-white text-[11px] font-bold shrink-0"
          style={{ backgroundColor: trade.otherUser?.color || '#6b7280' }}
        >
          {trade.otherUser?.initial || '?'}
        </div>
        <span className="flex-1 min-w-0 text-sm font-bold text-on-surface truncate">
          {trade.otherUser?.name}
        </span>
        <StateBadge kind={trade._kind} />
      </div>

      <div className="flex items-center gap-1.5 mt-2 ml-10">
        <CountBadge value={receiveCount} color="#15803d" />
        <CountBadge value={giveCount}    color="#b91c1c" />
        {trade._kind === 'active' && (
          <span className="ml-auto text-[10px] font-bold text-primary flex items-center gap-0.5">
            <span className="material-symbols-outlined text-[12px]">touch_app</span>
            {t('Coordinar')}
          </span>
        )}
      </div>

      {trade._kind === 'incoming' && (
        <div className="flex items-stretch gap-1 mt-2">
          <button
            type="button"
            disabled={busy}
            onClick={(e) => { e.stopPropagation(); onAccept(); }}
            className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg text-[11px] font-bold bg-primary text-on-primary disabled:opacity-60"
          >
            <span className="material-symbols-outlined text-sm">check</span>
            {t('Aceptar')}
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={(e) => { e.stopPropagation(); onCounter(); }}
            className="flex items-center justify-center px-2 py-1.5 rounded-lg text-[11px] font-bold border border-amber-400 text-amber-800 bg-amber-100 hover:bg-amber-200 disabled:opacity-60"
            title={t('Contraofertar')}
            aria-label={t('Contraofertar')}
          >
            <span className="material-symbols-outlined text-sm">swap_horiz</span>
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={(e) => { e.stopPropagation(); onReject(); }}
            className="flex items-center justify-center px-2 py-1.5 rounded-lg text-[11px] font-bold border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-60"
            title={t('Rechazar')}
            aria-label={t('Rechazar')}
          >
            <span className="material-symbols-outlined text-sm">close</span>
          </button>
        </div>
      )}
    </div>
  );
}

export default AlbumTradesPanel;
